/**
 * Лёгкий сбор событий старта приложения → POST /api/client-diagnostics/boot
 * Работает без axios (чтобы отчитаться даже если API-клиент не поднялся).
 */

import { detectInAppBrowser, isWebKit } from './inAppBrowser';

const SESSION_KEY = 'boot_diag_session';
const MAX_QUEUE = 25;
const FLUSH_DELAY_MS = 1500;
const IMMEDIATE_TYPES = new Set([
  'asset_load_error',
  'chunk_load_error',
  'react_mount_failed',
  'boot_watchdog_timeout',
  'boot_html_watchdog',
  'api_probe_fail',
]);

const queue = [];
let flushTimer = null;
let initialized = false;

function safeSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `anon-${Date.now().toString(36)}`;
  }
}

function apiBaseUrl() {
  const env = import.meta.env.VITE_API_URL;
  if (env) return env.replace(/\/$/, '');
  if (typeof window !== 'undefined') return `${window.location.origin}/api`;
  return '/api';
}

function getBuildHint() {
  const script = document.querySelector('script[type="module"]');
  return script?.getAttribute('src') || null;
}

function baseContext() {
  return {
    pageUrl: window.location.href,
    origin: window.location.origin,
    apiBase: apiBaseUrl(),
    userAgent: navigator.userAgent,
    online: navigator.onLine,
    visibility: document.visibilityState,
    hasToken: Boolean(localStorage.getItem('token')),
    appReady: document.documentElement.dataset.appReady || null,
    buildHint: getBuildHint(),
    inAppBrowser: detectInAppBrowser(),
    isWebKit: isWebKit(),
  };
}

function diagnosticsUrl() {
  return `${apiBaseUrl()}/client-diagnostics/boot`;
}

function scheduleFlush(immediate = false) {
  if (flushTimer) clearTimeout(flushTimer);
  if (immediate) {
    flushTimer = null;
    void flushQueue();
    return;
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, FLUSH_DELAY_MS);
}

async function flushQueue() {
  if (queue.length === 0) return;

  const payload = {
    sessionId: safeSessionId(),
    context: baseContext(),
    events: queue.splice(0, MAX_QUEUE),
  };

  const body = JSON.stringify(payload);
  const url = diagnosticsUrl();

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch { /* fallback to fetch */ }

  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      cache: 'no-store',
    });
  } catch (err) {
    console.warn('[boot-diag] send failed:', err.message);
  }
}

export function reportBootEvent(type, detail = {}) {
  queue.push({
    type,
    ts: Date.now(),
    detail,
  });

  if (queue.length > MAX_QUEUE) {
    queue.splice(0, queue.length - MAX_QUEUE);
  }

  scheduleFlush(IMMEDIATE_TYPES.has(type));
}

function fetchWithTimeout(url, ms = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { method: 'GET', cache: 'no-store', signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

/** Проверяет, отвечает ли API (тот же домен и опционально VITE_API_URL). */
export async function probeApiReachability() {
  const targets = new Set();
  targets.add(`${window.location.origin}/api/health`);

  const configured = import.meta.env.VITE_API_URL;
  if (configured) {
    const base = configured.replace(/\/$/, '');
    targets.add(`${base}/health`);
  }

  for (const url of targets) {
    const started = performance.now();
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await fetchWithTimeout(url, 8000);
        const ms = Math.round(performance.now() - started);
        reportBootEvent(res.ok ? 'api_probe_ok' : 'api_probe_fail', {
          url,
          status: res.status,
          ms,
          statusText: res.statusText,
          attempt,
        });
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
        if (attempt < 3) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 400 * attempt));
          continue;
        }
        const ms = Math.round(performance.now() - started);
        reportBootEvent('api_probe_fail', {
          url,
          ms,
          attempt,
          error: error?.name === 'AbortError' ? 'timeout' : (error?.message || 'network_error'),
        });
      }
    }
  }
}

export function initBootDiagnostics() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  reportBootEvent('boot_start');
  // Не конкурируем с auth verify и первым рендером на iOS
  const deferProbe = () => { void probeApiReachability(); };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(deferProbe, { timeout: 8000 });
  } else {
    setTimeout(deferProbe, 5000);
  }

  window.addEventListener('online', () => {
    reportBootEvent('api_probe_ok', { source: 'navigator_online' });
    void probeApiReachability();
  });

  window.addEventListener('offline', () => {
    reportBootEvent('api_probe_fail', { source: 'navigator_offline' });
  });

  window.addEventListener('pagehide', () => {
    scheduleFlush(true);
  });

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Отдельный тип: возврат на вкладку ≠ новая загрузка (иначе в мониторинге
      // выглядит как «зависшая» сессия с кучей boot_start без React).
      reportBootEvent('session_visible');
    }
  });
}

/** Для inline-скрипта в index.html (до загрузки React). */
export function installBootDiagnosticsGlobal() {
  if (typeof window === 'undefined') return;
  window.__reportBootDiag = (type, detail) => reportBootEvent(type, detail || {});
}
